import _ from 'lodash';
import { Action } from '@/OptimusRoutes/components/mapDevices/store/actions';
import {
	DevicesCoreData,
	MapDevices,
	Selected,
	SelectedDates,
} from '@/OptimusRoutes/components/mapDevices/types/mapDevicesReducer.types';
import { STANDARD_DATE_FORMAT, YEAR_MONTH_DAY_TIME_FORMAT } from '@/OptimusRoutes/constants';
import {
	LAST_EVENTS_LENGTH_SIZE,
	SELECT_ALL_GROUPS,
	SELECT_ALL_UNITS,
} from '@/OptimusRoutes/pages/MainMap/components/ControlPanel/constants';
import { ActionType } from '@/types/actionType';
import { dateToString, getTimeDifference, isNewDate, utcToLocal, utcToLocalTimezone } from '@/utilities/dateUtils';

type State = {
	/**
	 * devices is the Complete object of all the devices of the user with all its data
	 * e.g. {
	 * 		111: {
	 * 			id: 111,
	 * 				id: 111,
	 * 				pin: '1234567890'.
	 * 				description: 'John Doe Truck'
	 * 				shared: false,
	 * 				latitude: -88.432
	 * 		},
	 * 		...
	 * }
	 *
	 * Contains all device information including:
	 * - Device information (id, name, description)
	 * - Real-time telemetry (speed, latitude, longitude, sensors, speed, idle, etc...)
	 * - Other (signal, camera, video, trackingHistoryId, battery, etc...)
	 *
	 * Indexed by device ID for efficient lookups
	 */
	devices: MapDevices;
	/**
	 * devicesCoreData is a Lightweight device data for performance-critical operations
	 *
	 * Contains minimal essential data to prevent unnecessary component re-renders:
	 * - Device ID for identification
	 * - Pin for rabbit subscription
	 * - Description for UI labels
	 * - isTransmitting status for real-time indicators & UI elements
	 *
	 * This separation allows UI components to subscribe to only the data they need,
	 * improving performance by avoiding re-renders when heavy telemetry data changes (lat, lng, speed...)
	 */
	devicesCoreData: DevicesCoreData;
	/**
	 * Checks if all units are being fetched
	 */
	isLoadingUnits: boolean | null;
	/**
	 * mainMapSelected is the object containing the selected unit or units (if `Select All` is selected)
	 */
	mainMapSelected: Selected;
	/**
	 * segmented is the flags that tells if the trail on the map will be segmented or not.
	 */
	isSegmented: boolean;
	/**
	 * Date of the trail that will be shown
	 */
	selectedDates: SelectedDates | null;
};

const initialState: State = {
	devices: {},
	devicesCoreData: {},
	isLoadingUnits: null,
	isSegmented: false,
	selectedDates: null,
	mainMapSelected: {
		groups: {
			[SELECT_ALL_GROUPS]: {
				total: 0,
				units: [],
			},
		},
		unitId: null,
	},
};

const reducer = (state: State = initialState, action: Action): State => {
	switch (action.type) {
		case ActionType.MAP_DEVICES_SET_DEVICES:
			return {
				...state,
				devices: action.payload.devices,
				devicesCoreData: action.payload.devicesCoreData,
			};
		case ActionType.MAP_DEVICES_UPDATE_DEVICE: {
			const prevDatetime = state.devices[action.payload.id].utcDate;
			const isRecentDate = getTimeDifference(prevDatetime, action.payload.data.utcDate, 'seconds') > 0;
			if (!isRecentDate) {
				return state;
			}
			const { tanks: newTanks, thermometers: newThermometer, utcDate, ...restOfPayload } = action.payload.data;
			const deviceId = action.payload.id;

			const lastReportedDate = state.devices[deviceId].lastReportedEvent?.utcDate;
			const isNewEvent = isNewDate(utcDate, lastReportedDate);

			let lastEvents = state.devices[deviceId]?.lastEvents || [];
			if (isNewEvent) {
				const utcOffsetMinutes = state.devices[deviceId].utcOffsetMinutes;
				const newEvents =
					action.payload.eventsToUpdate?.map((lastIsolatedEvent) => ({
						eventId: lastIsolatedEvent.id,
						date: dateToString(utcToLocal(utcDate, utcOffsetMinutes), YEAR_MONTH_DAY_TIME_FORMAT),
					})) || [];

				lastEvents = [...newEvents, ...lastEvents];
				lastEvents = lastEvents.slice(0, LAST_EVENTS_LENGTH_SIZE);
			}

			let thermometers = state.devices[deviceId]?.thermometers || {};
			thermometers = {
				...thermometers,
				...action.payload.data.thermometers,
			};

			const oldTanks = state.devices[deviceId]?.tanks;
			const oldThermometers = state.devices[deviceId]?.thermometers;

			let tanks = state.devices[deviceId]?.tanks || {};
			tanks = { ...tanks, ...newTanks };

			let updatedState = {
				...state,
				devices: {
					...state.devices,
					[deviceId]: {
						...state.devices[deviceId],
						...restOfPayload,
						lastEvents,
					},
				},
			};

			const selectedDeviceId = state.mainMapSelected.unitId;
			if (selectedDeviceId && selectedDeviceId === deviceId) {
				const localDate = utcToLocalTimezone(action.payload.data.utcDate);
				const newSelectedDates = {
					initial: `${localDate} 00:00:00`,
					final: `${localDate} 23:59:59`,
				};
				const isSameDate = _.isEqual(newSelectedDates, state.selectedDates);
				if (!isSameDate) {
					updatedState.selectedDates = newSelectedDates;
				}
			}

			const isSameTanks = _.isEqual(oldTanks, newTanks);
			if (newTanks && !isSameTanks) {
				updatedState.devices[deviceId].tanks = tanks;
			}

			const isSameThermometers = _.isEqual(oldThermometers, newThermometer);
			if (newThermometer && !isSameThermometers) {
				updatedState.devices[deviceId].thermometers = thermometers;
			}

			const deviceToUpdateData = updatedState.devicesCoreData[deviceId];
			const hasNotBeenTransmitting = action.payload.data?.utcDate && !deviceToUpdateData?.isTransmiting;
			if (hasNotBeenTransmitting) {
				console.log('🔴 New Transmitting Device ', deviceToUpdateData.description); // eslint-disable-line no-console
				let groups = state.mainMapSelected.groups;
				if (state.mainMapSelected.groups?.[SELECT_ALL_GROUPS]) {
					const selectAllGroup = state.mainMapSelected.groups?.[SELECT_ALL_GROUPS];
					groups = {
						...groups,
						[SELECT_ALL_GROUPS]: {
							...selectAllGroup,
							units: [...selectAllGroup.units, deviceToUpdateData.id],
						},
					};
				}
				updatedState = {
					...updatedState,
					devicesCoreData: {
						...updatedState.devicesCoreData,
						[deviceId]: {
							...updatedState.devicesCoreData[deviceId],
							isTransmiting: true,
						},
					},
					mainMapSelected: {
						...state.mainMapSelected,
						groups,
					},
				};
			}

			return updatedState;
		}
		case ActionType.MAP_DEVICES_UPDATE_LAST_REPORT: {
			let updateLastEvent = state.devices[action.payload.id];
			updateLastEvent = {
				...updateLastEvent,
				...action.payload.data,
			};
			return {
				...state,
				devices: {
					...state.devices,
					[action.payload.id]: updateLastEvent,
				},
			};
		}
		case ActionType.MAP_DEVICES_SET_LOADING_STATUS:
			return {
				...state,
				isLoadingUnits: action.payload,
			};
		case ActionType.MAP_DEVICES_CLEAR_DEVICES:
			return {
				...state,
				devices: {},
				devicesCoreData: {},
			};
		case ActionType.MAP_DEVICES_CLEAR_ALL_DATA:
			return initialState;
		case ActionType.MAP_DEVICES_SET_LAST_EVENTS: {
			return {
				...state,
				devices: {
					...state.devices,
					[action.payload.deviceId]: {
						...state.devices[action.payload.deviceId],
						lastEvents: action.payload.lastEvents,
					},
				},
			};
		}
		case ActionType.MAP_DEVICES_UPDATE_LAST_EVENTS: {
			const { deviceId, lastEvents: newEventsList, utcDate } = action.payload;

			const lastReportedDate = state.devices[deviceId].lastReportedEvent?.utcDate;
			const utcOffsetMinutes = state.devices[deviceId].utcOffsetMinutes;
			const isNewEvent = isNewDate(action.payload.utcDate, lastReportedDate);

			let lastEvents = state.devices[deviceId]?.lastEvents || [];
			if (isNewEvent) {
				const newEvents =
					newEventsList?.map((lastIsolatedEvent) => ({
						eventId: lastIsolatedEvent.id,
						date: dateToString(utcToLocal(utcDate, utcOffsetMinutes), YEAR_MONTH_DAY_TIME_FORMAT),
					})) || [];

				lastEvents = [...newEvents, ...lastEvents];
				lastEvents = lastEvents.slice(0, LAST_EVENTS_LENGTH_SIZE);
			}

			return {
				...state,
				devices: {
					...state.devices,
					[deviceId]: {
						...state.devices[deviceId],
						lastEvents,
					},
				},
			};
		}
		/**
		 * * Start of Main Map Specific Reducers
		 */
		case ActionType.MAP_DEVICES_SET_SELECTED_DATE: {
			return {
				...state,
				selectedDates: action.payload,
			};
		}
		case ActionType.MAP_DEVICES_SET_SELECTED_GROUPS: {
			if (_.isEqual(action.payload, state.mainMapSelected.groups)) {
				return state;
			}
			console.log('🎯👥 Selecting new group units ', action.payload); // eslint-disable-line no-console

			const updatedState = {
				...state,
				mainMapSelected: {
					...state.mainMapSelected,
					groups: { ...state.mainMapSelected.groups, ...action.payload },
				},
			};
			return updatedState;
		}
		case ActionType.MAP_DEVICES_SET_SELECTED_UNIT: {
			const selectedUnitId = action.payload;
			if (selectedUnitId === state.mainMapSelected.unitId) {
				return state;
			}

			console.log('🎯👤 Selecting new group units ', selectedUnitId); // eslint-disable-line no-console

			let selectedDates: SelectedDates | null = state.selectedDates;
			if (selectedUnitId === SELECT_ALL_UNITS) {
				selectedDates = null;
			} else {
				const utcDate = state.devices[+selectedUnitId].utcDate;
				const utcOffsetMinutes = state.devices[+selectedUnitId].utcOffsetMinutes;
				const localDate = dateToString(utcToLocal(utcDate, utcOffsetMinutes), STANDARD_DATE_FORMAT);
				selectedDates = {
					initial: `${localDate} 00:00:00`,
					final: `${localDate} 23:59:59`,
				};
			}

			const updatedState = {
				...state,
				selectedDates,
				mainMapSelected: {
					...state.mainMapSelected,
					unitId: selectedUnitId,
				},
			};
			return updatedState;
		}
		case ActionType.MAP_DEVICES_SET_SELECTED_GROUP_CHILD: {
			const newGroups = { ...state.mainMapSelected.groups };
			const updatedGroup = newGroups[action.payload.groupId];
			if (updatedGroup) {
				updatedGroup.units = Array.from(new Set([...updatedGroup.units, action.payload.childId]));
			} else {
				newGroups[action.payload.groupId] = {
					total: action.payload.total ?? 0,
					units: [action.payload.childId],
				};
			}
			console.log('🎯👥 Selecting new group units CHILD ', action.payload); // eslint-disable-line no-console

			const updatedState = {
				...state,
				mainMapSelected: {
					...state.mainMapSelected,
					groups: newGroups,
				},
			};
			return updatedState;
		}
		case ActionType.MAP_DEVICES_REMOVE_SELECTED_GROUP: {
			if (state.mainMapSelected.groups && state.mainMapSelected.groups[action.payload]) {
				const newGroups = { ...state.mainMapSelected.groups };
				delete newGroups[action.payload];

				let selectedUnit = state.mainMapSelected.unitId;
				const noGroupsSelected = Object.keys(newGroups).length === 0;
				if (noGroupsSelected) {
					selectedUnit = SELECT_ALL_UNITS;
				}

				return {
					...state,
					mainMapSelected: {
						unitId: selectedUnit,
						groups: newGroups,
					},
				};
			}
			return state;
		}
		case ActionType.MAP_DEVICES_REMOVE_SELECTED_GROUP_CHILD: {
			const selectedGroups = { ...state.mainMapSelected.groups };
			const updatedUnits = selectedGroups[action.payload.groupId]?.units.filter(
				(unitId) => unitId !== action.payload.childId
			);
			if (updatedUnits.length === 0) {
				delete selectedGroups[action.payload.groupId];
			} else {
				selectedGroups[action.payload.groupId].units = updatedUnits;
			}

			const updatedState = {
				...state,
				mainMapSelected: {
					...state.mainMapSelected,
					groups: selectedGroups,
				},
			};
			return updatedState;
		}
		/**
		 * * End of Main Map Specific Reducers
		 */
		default:
			return state;
	}
};

export default reducer;
